import Swal from 'sweetalert2';
import toast from 'react-hot-toast';

export interface AlertOptions {
  title?: string;
  message: string;
  type?: 'info' | 'success' | 'warning' | 'error';
  confirmButtonText?: string;
  showCancelButton?: boolean;
  cancelButtonText?: string;
  allowOutsideClick?: boolean;
  timer?: number;
}

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'error' | 'info' | 'question';
  dangerMode?: boolean;
}

class NotificationService {
  // Basic notification methods (keep toast for simple, quick feedback)
  success(message: string, duration: number = 4000) {
    return toast.success(message, { duration });
  }

  error(message: string, duration: number = 4000) {
    return toast.error(message, { duration });
  }

  info(message: string, duration: number = 4000) {
    return toast(message, {
      duration,
      icon: 'ℹ️',
      style: {
        borderLeft: '4px solid #3498db',
      },
    });
  }

  warning(message: string, duration: number = 4000) {
    return toast(message, {
      duration,
      icon: '⚠️',
      style: {
        borderLeft: '4px solid #f39c12',
        background: '#fff3cd',
        color: '#856404',
      },
    });
  }

  // SweetAlert2-based alert for important messages
  async alert(options: AlertOptions) {
    const { 
      title, 
      message, 
      type = 'info', 
      confirmButtonText = 'OK',
      showCancelButton = false,
      cancelButtonText = 'Cancel',
      allowOutsideClick = true,
      timer
    } = options;

    const result = await Swal.fire({
      title,
      text: message,
      icon: type,
      confirmButtonText,
      showCancelButton,
      cancelButtonText,
      allowOutsideClick,
      timer,
      customClass: {
        popup: 'rounded-lg',
        confirmButton: 'bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg mx-2',
        cancelButton: 'bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded-lg',
      },
      buttonsStyling: false,
    });

    return result;
  }

  // Enhanced confirmation dialog with SweetAlert2
  async confirm(options: ConfirmOptions): Promise<boolean> {
    const {
      title = 'Are you sure?',
      message,
      confirmText = 'Yes',
      cancelText = 'No',
      type = 'question',
      dangerMode = false,
    } = options;

    const result = await Swal.fire({
      title,
      text: message,
      icon: type,
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      reverseButtons: true,
      customClass: {
        popup: 'rounded-lg',
        confirmButton: dangerMode 
          ? 'bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg mx-2'
          : 'bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg mx-2',
        cancelButton: 'bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded-lg',
      },
      buttonsStyling: false,
    });

    return result.isConfirmed;
  }

  // Coming soon notification for features under development
  async comingSoon(featureName: string = 'This feature') {
    return await this.alert({
      title: '🚧 Coming Soon',
      message: `${featureName} is currently under development and will be available in a future update.`,
      type: 'info',
      confirmButtonText: 'Got it',
      timer: 5000,
    });
  }

  // Loading dialog for long operations
  showLoading(title: string = 'Loading...', message: string = 'Please wait...') {
    Swal.fire({
      title,
      text: message,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  }

  // Close loading dialog
  hideLoading() {
    Swal.close();
  }

  // Success with auto-close
  async showSuccess(title: string, message?: string, timer: number = 3000) {
    return await Swal.fire({
      title,
      text: message,
      icon: 'success',
      timer,
      showConfirmButton: false,
      customClass: {
        popup: 'rounded-lg',
      },
    });
  }

  // Error dialog
  async showError(title: string, message?: string) {
    return await Swal.fire({
      title,
      text: message,
      icon: 'error',
      confirmButtonText: 'OK',
      customClass: {
        popup: 'rounded-lg',
        confirmButton: 'bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg',
      },
      buttonsStyling: false,
    });
  }

  // Input dialog
  async input(options: {
    title: string;
    message?: string;
    inputType?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
    inputPlaceholder?: string;
    inputValue?: string;
    confirmButtonText?: string;
    cancelButtonText?: string;
  }) {
    const {
      title,
      message,
      inputType = 'text',
      inputPlaceholder = '',
      inputValue = '',
      confirmButtonText = 'OK',
      cancelButtonText = 'Cancel',
    } = options;

    const result = await Swal.fire({
      title,
      text: message,
      input: inputType,
      inputPlaceholder,
      inputValue,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      customClass: {
        popup: 'rounded-lg',
        confirmButton: 'bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg mr-2',
        cancelButton: 'bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded-lg',
      },
      buttonsStyling: false,
    });

    return result;
  }

  // Dismiss all notifications (both toast and SweetAlert)
  dismissAll() {
    toast.dismiss();
    Swal.close();
  }
}

// Create a singleton instance
export const notifications = new NotificationService();

// Export individual methods for convenience
export const {
  success,
  error,
  info,
  warning,
  alert,
  confirm,
  comingSoon,
  dismissAll,
} = notifications;